/**
 * useInvoiceDecision — the Rechnungskontrolle decision mutation (Story UI-U4).
 *
 * All human decisions go through the EXISTING E10 endpoints (no invented writes):
 *   { kind:'handle' }                        → POST /admin/invoice-check/:id/handle
 *   { kind:'override', newCategory, reason } → POST …/override  (reason ≥10 server rule)
 *   { kind:'orchestrate' }                   → POST …/orchestrate ("Zuordnung bestätigen")
 *
 * OPTIMISTIC (useEingangDecision pattern): onMutate snapshots + patches the
 * rows cache with the decision's resulting status; onError rolls back + toasts;
 * onSettled invalidates rows + checkDetail + history. The UI renders the SERVER
 * verdict — the optimistic status is only the requested action's display.
 *
 * [Source: apps/web/src/features/communication-v2/hooks/useEingangMails.ts#useEingangDecision]
 * [Source: docs/stories/ui-u4-rechnungskontrolle.md AC3]
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { InvoiceCheckStatus } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import type { InvoiceRow } from '../types';

/** A human decision on one invoice — mapped to exactly one as-built E10 endpoint. */
export type InvoiceDecision =
  | { kind: 'handle' }
  | { kind: 'override'; newCategory: 'K1' | 'K3'; reason: string }
  | { kind: 'orchestrate' };

/** The optimistic list status for a decision (undefined = leave as-is; e.g. orchestrate). */
function optimisticStatus(decision: InvoiceDecision): InvoiceCheckStatus | undefined {
  if (decision.kind === 'handle') return 'released';
  if (decision.kind === 'override') return decision.newCategory === 'K1' ? 'released' : 'blocked';
  return undefined; // orchestrate: the chain result is not knowable client-side
}

function dispatch(id: string, decision: InvoiceDecision): Promise<unknown> {
  switch (decision.kind) {
    case 'handle':
      return apiClient.adminInvoiceCheck.handle(id);
    case 'override':
      return apiClient.adminInvoiceCheck.override(id, {
        newCategory: decision.newCategory,
        reason: decision.reason,
      });
    case 'orchestrate':
      return apiClient.adminInvoiceCheck.orchestrate(id);
  }
}

export function useInvoiceDecision(onErrorToast?: (message: string) => void) {
  const qc = useQueryClient();
  const listKey = qk.invoices({ mode: isLiveData() ? 'live' : 'mock' });

  return useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: InvoiceDecision }) =>
      dispatch(id, decision),

    onMutate: async ({ id, decision }) => {
      await qc.cancelQueries({ queryKey: listKey });
      const previous = qc.getQueryData<InvoiceRow[]>(listKey);
      const next = optimisticStatus(decision);
      if (next) {
        qc.setQueryData<InvoiceRow[]>(listKey, (old) =>
          old?.map((r) => (r.id === id ? { ...r, rawStatus: next } : r)),
        );
      }
      return { previous };
    },

    onError: (_error, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(listKey, context.previous);
      }
      onErrorToast?.('Entscheid fehlgeschlagen, Änderung zurückgesetzt.');
    },

    onSettled: (_data, _error, vars) => {
      void qc.invalidateQueries({ queryKey: listKey });
      void qc.invalidateQueries({ queryKey: qk.invoiceCheckDetail(vars.id) });
      void qc.invalidateQueries({ queryKey: qk.invoiceHistory(vars.id) });
    },
  });
}
